<template>
  <QuickActionButton
    @click="meterConfigsModal?.$el?.present()"
    icon="icon-meter"
    title="Meter Configurations"
    subtitle="See an overview of which meter models are installed on this property"
  ></QuickActionButton>
  <IonModal ref="meterConfigsModal" @ion-modal-will-present="onWillPresent">
    <IonHeader class="ion-no-border px-[10px] bg-white">
      <IonToolbar color="white" class="border-b-2 border-gray-300">
        <IonTitle color="medium"><i class="icon-meter mr-2"></i>Meters</IonTitle>
        <IonButtons slot="end">
          <IconButton icon="icon-x" @click="meterConfigsModal?.$el?.dismiss()"></IconButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent color="white" class="bg-white">
      <div v-if="!meterTypeGroups.length" class="text-gray-500 text-center">
        <div class="font-medium italic m-8">Looks like no meters have been installed on this property yet.</div>
        <div class="mx-4">To start setting up meters</div>
        <BaseClearButton @click="navToUnits()" size="small"><i class="icon-eye mr-2"></i>Veiw Units</BaseClearButton>
      </div>
      <div v-for="g of meterTypeGroups" class="m-2">
        <SectionTitle
          ><i :class="g.utilityType.iconName" class="mr-2" :style="{ color: g.utilityType.colorHex }"></i
          >{{ g.utilityType.name }}</SectionTitle
        >
        <IonList inset>
          <MeterTypeFormGroup
            disabled
            :utility-type-id="t.utilityType.id"
            :manufacturer-id="t.meterManufacturerId"
            :meter-type-id="t.meterTypeId"
            :uom-type-id="t.uomTypeId"
            :multiplier="t.multiplier"
            :meter-size="t.meterSize"
            :used-count="t.usedCount"
            v-for="t of g.types"
          ></MeterTypeFormGroup>
        </IonList>
      </div>
    </IonContent>
  </IonModal>
</template>

<script setup lang="ts">
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import IconButton from '@/components/forms/IconButton.vue';
import MeterTypeFormGroup from '@/components/forms/meters/MeterTypeFormGroup.vue';
import QuickActionButton from '@/components/forms/QuickActionButton.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import { SelectOption } from '@/components/forms/select';
import { MeterTypeService } from '@/http-services/meter-type.service';
import { useSelectedProperty } from '@/stores/selected-property.store';
import {
  createAnimation,
  IonButtons,
  IonContent,
  IonHeader,
  IonList,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from '@ionic/vue';
import { IUtilityType, UomTypeHelper, UomTypeIds } from '@ncss/models';
import { MobileMeterType } from 'common';
import { ref, useTemplateRef } from 'vue';

interface MeterTypeGroup {
  utilityType: IUtilityType;
  types: MobileMeterType[];
  uomOptions: SelectOption<UomTypeIds>[];
}

const meterConfigsModal = useTemplateRef('meterConfigsModal');
const propertyStore = useSelectedProperty();
const meterTypeService = new MeterTypeService();
const meterTypeGroups = ref<MeterTypeGroup[]>([]);
const router = useIonRouter();

function navToUnits() {
  meterConfigsModal.value?.$el?.dismiss();
  setTimeout(() => {
    if (propertyStore.property) {
      router.push(`/units/${propertyStore.property._id}`);
    }
  }, 300);
}

async function onWillPresent() {
  if (propertyStore.property) {
    const results: MeterTypeGroup[] = [];
    const meterTypes = await meterTypeService.getUsedMeterTypes(propertyStore.property._id);
    for (const mt of meterTypes) {
      let index = results.findIndex((r) => r.utilityType.id === mt.utilityType.id);
      if (index === -1) {
        const uomOptions = UomTypeHelper.GetUomForUtilityTypeId(mt.utilityType.id).map((uom) => {
          return {
            label: uom.name,
            value: uom.id,
          };
        });
        results.push({ utilityType: mt.utilityType, types: [], uomOptions });
        index = results.length - 1;
      }
      results[index].types.push(mt);
    }
    meterTypeGroups.value = results;
  }
}
</script>

<style scoped></style>
